/**
 * Los mensajes que trae el servidor, como avisos de esquina
 * -----------------------------------------------------------------------------
 * Symfony los llama *flash*: se guardan al redirigir y se leen una vez en la
 * página siguiente. Hasta ahora cada aplicación los pintaba como un `.alert`
 * metido al principio del `<main>`, y eso tenía tres problemas.
 *
 * Empuja el contenido hacia abajo, así que la pantalla salta justo cuando la
 * estás leyendo. No se va nunca, y un «quedó guardado» de hace diez minutos
 * sigue ahí diciendo que algo acaba de pasar. Y **cada aplicación se inventó su
 * propio mapa de colores**, con el resultado de que en Inventarios «esa bodega
 * ya tiene una ubicación con ese código» salía en verde, y en Calendario «la
 * fecha de inicio no se entiende» también.
 *
 * Aquí se usan los avisos del tema -{@see ./notify.js}-: esquina superior
 * derecha, se van solos, se paran si pasas el ratón por encima.
 *
 * ## El mapa vive aquí y no en cada plantilla
 *
 * Es la razón de que exista este fichero. Las seis aplicaciones usan las mismas
 * palabras y cada una las traducía a su manera; con el mapa repartido, arreglar
 * el verde de Inventarios no arreglaba el de Calendario. Ahora una palabra
 * nueva se añade en un sitio y las seis la entienden igual.
 *
 * ## Cómo lo manda el servidor
 *
 * En un bloque de datos, no en HTML ya pintado:
 *
 *     <script type="application/json" data-owari-avisos>
 *       [{"tipo": "aviso", "texto": "La ficha quedó guardada."}]
 *     </script>
 *
 * JSON y no atributos sueltos porque un mensaje lleva comillas, tildes y a
 * veces el nombre que escribió alguien. Con `textContent` no hay forma de que
 * eso se convierta en marcado.
 */

import { notify } from './notify.js';

/**
 * De la palabra que usa el servidor al tipo de aviso.
 *
 * Están las de las seis aplicaciones porque el bundle es el mismo para todas.
 * Lo que no esté aquí sale como `info`, que es lo que no miente: se ve, no se
 * pinta de verde y no dice que algo falló.
 */
const TIPOS = {
  // Se hizo, y salió bien.
  aviso: 'success',
  bien: 'success',
  exito: 'success',
  success: 'success',

  // No se hizo, o se hizo mal.
  error: 'error',
  danger: 'error',
  problema: 'error',
  fallo: 'error',

  // Se hizo, pero hay algo que mirar.
  warning: 'warning',
  atencion: 'warning',

  info: 'info',
};

/** Lo que no es un mensaje y no debe salir como tal. */
const NO_SON_MENSAJES = ['secreto'];

/**
 * @param {string} etiqueta
 * @returns {string}
 */
export function tipoDeAviso(etiqueta) {
  return TIPOS[String(etiqueta).toLowerCase()] || 'info';
}

/**
 * Lee los bloques y los enseña.
 *
 * El bloque se vacía después de leerlo. Sin eso, un `autoInit()` posterior
 * -que se llama al inyectar HTML- volvería a sacar los mismos avisos.
 *
 * @param {Document|Element} [raiz]
 */
export function mostrarAvisosDelServidor(raiz = document) {
  const bloques = raiz.querySelectorAll('script[type="application/json"][data-owari-avisos]');

  bloques.forEach((bloque) => {
    const crudo = bloque.textContent;

    // Se vacía antes de pintar: si algo de abajo lanza, no se repite en el
    // siguiente arranque.
    bloque.textContent = '';

    let avisos;

    try {
      avisos = JSON.parse(crudo || '[]');
    } catch (error) {
      // Un JSON roto no vale para tumbar la página: el usuario se queda sin el
      // mensaje, que es malo, pero puede seguir trabajando.
      return;
    }

    if (!Array.isArray(avisos)) return;

    avisos.forEach((aviso) => {
      const texto = aviso && typeof aviso.texto === 'string' ? aviso.texto.trim() : '';

      if (texto === '' || NO_SON_MENSAJES.includes(aviso.tipo)) return;

      const tipo = tipoDeAviso(aviso.tipo);

      notify[tipo](texto);
    });
  });
}
